import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Phone, X, User, Building, Clock, PhoneCall, PhoneMissed, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import axios from 'axios';

const CallNotification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [callStatus, setCallStatus] = useState('ringing');
  const [duration, setDuration] = useState(0);
  const [contactInfo, setContactInfo] = useState(null);

  // Get 3CX parameters from URL
  const callerNumber = searchParams.get('phoneNumber') || searchParams.get('CallerNumber');
  const callerDisplayName = searchParams.get('displayName') || searchParams.get('CallerDisplayName');

  useEffect(() => {
    // Try to find matching contact from CRM
    const run = async () => {
      if (!callerNumber) return;

      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      if (base) {
        try {
          const { data } = await axios.get(`${base}/api/contacts/lookup`, { params: { phone: callerNumber } });
          if (data?.id) {
            setContactInfo({
              name: data.name,
              company: data.company || '',
              phone: callerNumber,
              avatar: (data.name || 'C').substring(0, 2).toUpperCase(),
              email: data.email,
              status: data.status || 'Active',
              dealValue: data.deal_value ?? null,
              lastContact: data.last_contact ?? null
            });
            return;
          }
        } catch {/* fall back */}
      }

      setContactInfo({
        name: callerDisplayName || 'Unknown Caller',
        company: 'Not in CRM',
        phone: callerNumber,
        avatar: (callerDisplayName || 'U').substring(0, 2).toUpperCase(),
        email: null,
        status: 'New Lead',
        dealValue: null,
        lastContact: null
      });
    };

    run();
  }, [callerNumber, callerDisplayName]);

  useEffect(() => {
    if (callStatus === 'answered') {
      const interval = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callStatus]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = () => {
    setCallStatus('answered');
    // In real implementation, this would signal 3CX to route the call
  };

  const handleDecline = () => {
    setCallStatus('declined');
    // In real implementation, this would signal 3CX to reject the call
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  const handleEndCall = () => {
    setCallStatus('ended');
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  const handleAddContact = () => {
    navigate('/dashboard', { state: { addContact: contactInfo } });
  };

  if (!contactInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading call information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="shadow-2xl">
          <CardContent className="p-8">
            {/* Call Status Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                {callStatus === 'ringing' && (
                  <>
                    <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
                    <span className="text-lg font-semibold text-blue-600">Incoming Call</span>
                  </>
                )}
                {callStatus === 'answered' && (
                  <>
                    <div className="w-4 h-4 rounded-full bg-green-500 animate-pulse" />
                    <span className="text-lg font-semibold text-green-600">Call in Progress</span>
                  </>
                )}
                {callStatus === 'declined' && (
                  <>
                    <div className="w-4 h-4 rounded-full bg-red-500" />
                    <span className="text-lg font-semibold text-red-600">Call Declined</span>
                  </>
                )}
                {callStatus === 'ended' && (
                  <>
                    <div className="w-4 h-4 rounded-full bg-gray-500" />
                    <span className="text-lg font-semibold text-gray-600">Call Ended</span>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                3CX Integration Active
              </div>
            </div>

            {/* Caller Info */}
            <div className="text-center mb-8">
              <Avatar className="h-24 w-24 mx-auto mb-4">
                <AvatarFallback className="bg-blue-500 text-white text-3xl">
                  {contactInfo.avatar}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{contactInfo.name}</h2>
              {contactInfo.company && (
                <p className="text-lg text-gray-600 flex items-center justify-center">
                  <Building className="w-5 h-5 mr-2" />
                  {contactInfo.company}
                </p>
              )}
              <p className="text-xl text-gray-500 mt-2 font-mono">{contactInfo.phone}</p>
            </div>

            {/* Call Duration */}
            {callStatus === 'answered' && (
              <div className="mb-6 p-6 bg-green-50 rounded-xl text-center border-2 border-green-200">
                <div className="flex items-center justify-center space-x-3">
                  <Clock className="w-6 h-6 text-green-600" />
                  <span className="text-4xl font-mono font-bold text-green-600">
                    {formatDuration(duration)}
                  </span>
                </div>
              </div>
            )}

            {/* Contact Information Card */}
            {contactInfo.email && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
                <h3 className="font-semibold text-gray-900 mb-3">Contact Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {contactInfo.email && (
                    <div>
                      <p className="text-gray-500">Email</p>
                      <p className="font-medium">{contactInfo.email}</p>
                    </div>
                  )}
                  {contactInfo.status && (
                    <div>
                      <p className="text-gray-500">Status</p>
                      <p className="font-medium">{contactInfo.status}</p>
                    </div>
                  )}
                  {contactInfo.dealValue && (
                    <div>
                      <p className="text-gray-500">Deal Value</p>
                      <p className="font-medium text-green-600">${contactInfo.dealValue.toLocaleString()}</p>
                    </div>
                  )}
                  {contactInfo.lastContact && (
                    <div>
                      <p className="text-gray-500">Last Contact</p>
                      <p className="font-medium">{new Date(contactInfo.lastContact).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Call Actions */}
            {callStatus === 'ringing' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={handleDecline}
                    variant="outline"
                    size="lg"
                    className="h-16 border-2 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <PhoneMissed className="w-5 h-5 mr-2" />
                    Decline
                  </Button>
                  <Button
                    onClick={handleAnswer}
                    size="lg"
                    className="h-16 bg-green-500 hover:bg-green-600 text-white text-lg"
                  >
                    <PhoneCall className="w-5 h-5 mr-2" />
                    Answer
                  </Button>
                </div>
                {contactInfo.company === 'Not in CRM' && (
                  <Button
                    onClick={handleAddContact}
                    variant="outline"
                    className="w-full"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Add to Contacts
                  </Button>
                )}
              </div>
            )}

            {callStatus === 'answered' && (
              <Button
                onClick={handleEndCall}
                className="w-full h-16 bg-red-500 hover:bg-red-600 text-white text-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                End Call
              </Button>
            )}

            {(callStatus === 'declined' || callStatus === 'ended') && (
              <div className="text-center">
                <p className="text-gray-500 mb-4">
                  {callStatus === 'declined' ? 'Call was declined' : `Call ended after ${formatDuration(duration)}`}
                </p>
                <Button onClick={() => navigate('/dashboard')} variant="outline">
                  Return to Dashboard
                </Button>
              </div>
            )}

            {/* 3CX Info */}
            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-500">
                Call received via 3CX • {new Date().toLocaleTimeString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CallNotification;